import { useCallback, useEffect, useState } from 'react'
import { supabase } from '~/supabase-client'
import { AuthContext } from './auth-context'

const TICKET_STORAGE_KEY = 'terra_ticket_info'

const checkProfileExists = async (userId) => {
  const { data } = await supabase.from('users').select('id').eq('id', userId).maybeSingle()
  return !!data
}

export const AuthContextProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const [ticketInfo, setTicketInfo] = useState(null)
  const [needsProfileSetup, setNeedsProfileSetup] = useState(false)

  // Load persisted ticket info on mount
  useEffect(() => {
    const stored = localStorage.getItem(TICKET_STORAGE_KEY)
    if (stored) {
      try {
        const parsed = JSON.parse(stored)
        // Check if ticket is still valid (not expired)
        if (parsed.expiresAt && new Date(parsed.expiresAt) > new Date()) {
          setTicketInfo({
            ticketId: parsed.ticketId,
            expiresAt: new Date(parsed.expiresAt)
          })
        } else {
          // Expired, clear storage
          localStorage.removeItem(TICKET_STORAGE_KEY)
        }
      } catch (e) {
        localStorage.removeItem(TICKET_STORAGE_KEY)
      }
    }
  }, [])

  useEffect(() => {
    // onAuthStateChange fires INITIAL_SESSION on subscribe, covering the getSession() case.
    // Handling both INITIAL_SESSION and SIGNED_IN here avoids the race where INITIAL_SESSION
    // fires with a session (e.g. magic link code exchange) before needsProfileSetup is set.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      const needsProfileCheck = session && (event === 'SIGNED_IN' || event === 'INITIAL_SESSION')

      // Keep loading true during the async profile check so redirects don't fire early
      if (needsProfileCheck) setLoading(true)

      setSession(session)
      setUser(session?.user ?? null)

      try {
        if (!session) {
          setNeedsProfileSetup(false)
        } else if (needsProfileCheck) {
          const hasProfile = await checkProfileExists(session.user.id)
          setNeedsProfileSetup(!hasProfile)
        }
      } catch (e) {
        console.error('Auth profile check failed:', e)
        setNeedsProfileSetup(false)
      } finally {
        setLoading(false)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  const markProfileComplete = useCallback(() => {
    setNeedsProfileSetup(false)
  }, [])

  const setTicketSession = useCallback((ticketId, expiresAt) => {
    const info = { ticketId, expiresAt }
    setTicketInfo(info)
    // Persist to localStorage
    localStorage.setItem(TICKET_STORAGE_KEY, JSON.stringify({
      ticketId,
      expiresAt: expiresAt.toISOString()
    }))
  }, [])

  const signOut = async () => {
    const { error } = await supabase.auth.signOut()
    if (error) {
      // If logout fails (e.g., 403 from local Supabase), force clear the session
      console.warn('Logout API call failed, clearing local session:', error)
      localStorage.removeItem('supabase.auth.token')
      setSession(null)
      setUser(null)
    }
    // Clear ticket info on sign out
    setTicketInfo(null)
    localStorage.removeItem(TICKET_STORAGE_KEY)
  }

  const value = {
    user,
    session,
    loading,
    signOut,
    ticketInfo,
    setTicketSession,
    needsProfileSetup,
    markProfileComplete,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
